import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import decorationEllipse from "../../../../../assets/landing/icons/ecosystem-icon/decoration-ellipse-card.png";
import { useMediaQuery } from "usehooks-ts";
import { useRef, useState, JSX } from "react";

type EcosystemCardProps = {
  title: string;
  desc: string;
  stepIcon: JSX.Element;
  stepInfo: string;
  cardBgImg: string;
  isLeftSide: boolean;
  descWidth: number;
  currentStep?: number;
  step: number;
  hoverRotation: () => void;
};

const EcosystemCard: React.FC<EcosystemCardProps> = ({
  title,
  desc,
  stepIcon: StepIcon,
  stepInfo,
  cardBgImg,
  isLeftSide = false,
  descWidth,
  currentStep,
  step,
  hoverRotation,
}) => {
  const matches = useMediaQuery("(min-width: 1024px)");
  const cardRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  return (
    <motion.article
      ref={cardRef}
      onMouseEnter={() => {
        setIsHovering(true);

        if (matches) {
          hoverRotation();
        }
      }}
      onMouseLeave={() => setIsHovering(false)}
      whileHover={{
        scale: 1.02,
      }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className={clsx(
        "bg-transparent p-[2.571vw] pb-[2.857vw] max-lg:p-[4.26vw] max-lg:rounded-[6.4vw] relative w-full group cursor-pointer z-10 max-lg:bg-[#F3F3F8]",
        currentStep === step && "max-lg:!bg-cardGradient"
      )}
    >
      <div className="absolute inset-0 z-0 max-lg:hidden">
        <img
          src={cardBgImg}
          alt="background decoration"
          className="w-[50.21vw] h-[21.51vw]"
        />
      </div>
      <AnimatePresence mode="wait">
        <AnimatePresence mode="wait">
          {isHovering && (
            <motion.img
              key="ellipse"
              src={decorationEllipse}
              alt="decoration ellipse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className={clsx(
                "absolute bottom-[1.58vw] lg:w-[27vw] pointer-events-none z-5 h-auto right-0 scale-110",
                isLeftSide && "left-0",
                step === 3 && "bottom-[0.3vw]"
              )}
            />
          )}
        </AnimatePresence>
      </AnimatePresence>
      <div
        className={clsx(
          "flex flex-col gap-[3.428vw] max-lg:gap-[6.4vw] lg:items-end max-lg:flex-col-reverse relative z-10",
          isLeftSide && "lg:!items-start"
        )}
      >
        <div
          className={clsx(
            "bg-[#E9EBF3] max-lg:w-full max-lg:justify-between  h-[4.571vw] max-lg:h-[14.93vw] max-lg:flex-row-reverse max-lg:rounded-[5.33vw] transition-all duration-300 lg:group-hover:bg-hoverBg max-lg:pl-[4.26vw] max-lg:pr-[2.13vw] pl-[0.6vw] flex pr-[1.428vw] w-fit items-center gap-[1.142vw] custom-shadow ecosystem-border",
            currentStep === step && "max-lg:bg-hoverBg"
          )}
        >
          <div className="size-[3.428vw] max-lg:size-[10.66vw] flex items-center justify-center bg-white max-lg:rounded-[3.2vw] rounded-[0.857vw] custom-shadow">
            {StepIcon}
          </div>
          <h4 className="text-black font-luxora-regular leading-[1.1] group-hover:lg:text-white transition-colors duration-300 text-20 max-lg:text-[4.26vw]">
            {stepInfo}
          </h4>
        </div>

        <div
          className={clsx(
            "flex flex-col gap-[1.142vw] max-lg:gap-[2.13vw] lg:items-end",
            isLeftSide && "lg:!items-start"
          )}
        >
          <h3 className="text-black font-normal font-luxora-regular leading-[1.1] text-36 max-lg:text-[8.53vw]">
            {title}
          </h3>
          <p
            style={{ maxWidth: matches ? `${descWidth}vw` : "100%" }}
            className={clsx(
              "font-normal leading-[1.1] text-[#00000099] text-16 max-lg:text-[3.73vw]",
              !isLeftSide && "lg:text-right"
            )}
          >
            {desc}
          </p>
        </div>
      </div>
    </motion.article>
  );
};

export default EcosystemCard;
